/**
 * Role presets — maps role names to capability arrays.
 *
 * The single source of truth: the client and both Cloud Functions codebases
 * import it through `@sendly/shared` and resolve capabilities from here.
 *
 * `firestore.rules` does NOT mirror this table — it checks one role string
 * (`isGymAdmin`) plus hand-written cap helpers and ad-hoc `extraCaps.hasAny`
 * terms (`isGymSetter` was deleted once its last caller moved to a cap). The
 * `hasCap()` helper, its generated preset table and the `gen:rules` codegen
 * were removed in 2c465cf because no rule ever called them, so changing a
 * preset here needs no rules step.
 *
 * ⚠️ EXCEPT for the hand-written role LISTS in the cap helpers there
 * (`hasRouteCap` → ["setter","gymAdmin"], `hasIntervalTimerPresetCap` →
 * ["setter","coach","receptionist","gymAdmin"], `hasTvManageCap` →
 * ["receptionist","gymAdmin"], `hasSprayWallCap` → ["setter","gymAdmin"] — it
 * also refuses banned members). Those mirror THIS table by hand and nothing
 * checks them: adding one of those caps to another preset here needs the
 * matching list widened in `firestore.rules` too.
 *
 * ⚠️ AND for the helpers whose preset arm is a single `role == "gymAdmin"`
 * comparison, which is faithful ONLY while the cap lives in the `gymAdmin`
 * preset alone. That includes the parameterised `hasGymAdminOnlyCap(gymId,
 * cap)` (`stats.viewAdvanced`, `gym.editProfile`) and `hasMauReadCap(gymId)`
 * (`billing.view` OR `stats.viewAdvanced`). Moving one of those caps into a
 * second preset here silently locks that role out of a surface the UI shows.
 */

export const GYM_ROLE_PRESETS = {
  user: [],

  setter: [
    'routes.create',
    'routes.edit',
    'routes.archive',
    'routes.viewTab',
    'intervalTimer.managePresets',
    'spraywall.manage', 'spraywall.moderate',
  ],

  coach: [
    'intervalTimer.managePresets',
    'events.checkIn',
    'competitions.viewClimberTimeline',
  ],

  receptionist: [
    'tv.manage',
    'tv.forceReleaseTimer',
    'intervalTimer.managePresets',
  ],

  gymAdmin: [
    // routes
    'routes.create', 'routes.edit', 'routes.archive', 'routes.delete', 'routes.viewTab',
    // users
    'users.view', 'users.viewProfile', 'users.changeRole', 'users.kick', 'users.ban', 'users.viewAuditLog',
    // gym profile
    'gym.editProfile', 'gym.editBranding', 'gym.editVisibility', 'gym.editGeneral',
    // gym config
    'maps.manage', 'difficulty.manage', 'holdColors.manage',
    // tv
    'tv.manage', 'tv.regenerateToken', 'tv.forceReleaseTimer',
    // stats
    'stats.viewSetterTab', 'stats.viewAdvanced',
    // competitions & series
    'competitions.manage', 'competitions.viewClimberTimeline', 'series.manage',
    // db
    'db.export', 'db.maintain',
    // billing
    'billing.view', 'billing.changeCurrency',
    // interval timer
    'intervalTimer.managePresets',
    // events
    'events.manage', 'events.checkIn',
    // MCP server (paid add-on)
    'mcp.read', 'mcp.write',
    // Spraywall (paid add-on)
    'spraywall.manage', 'spraywall.moderate',
  ],
};

export const PLATFORM_ROLE_PRESETS = {
  platformAdmin: [
    'platform.gym.create', 'platform.gym.activate', 'platform.gym.delete',
    'platform.gym.editPlan', 'platform.gym.editFeatures', 'platform.gym.editBilling',
    'platform.gym.assignAdmin', 'platform.gym.adminAny',
    'platform.stats.view', 'platform.pricing.edit', 'platform.settings.edit',
    'platform.user.editCaps', 'platform.viewAuditLog',
    'platform.recap.manage', 'platform.crashLogs.view',
    'platform.viewMarketData', 'platform.users.view', 'platform.users.ban',
    'platform.mcp.use', 'platform.gym.editAddons',
  ],
};

/** All valid gym role keys */
export const GYM_ROLES = Object.keys(GYM_ROLE_PRESETS);
